import React from 'react';
import { Button } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import { createOptionsList } from '../../services/common/miscUtils';
import { getMaxDateOfIncorporationDate } from '../../services/common/validation';
import {
  CompanyClassificationEnum,
  CorrespondenceMethodEnum,
  CountryCodeEnum,
  EnterpriseSizeEnum,
  EntityFormEnum,
  IdentityTypeEnum,
  InstitutionalTypeEnum,
  NoYesEnum,
} from '../../types/forms';
import { DatePickerFormField } from '../Fieldset/DatePickerFormField';
import { NumericInputFormField } from '../Fieldset/NumericInputFormField';
import { PhoneNumberFormField } from '../Fieldset/PhoneNumberFormField';
import { SelectFormField } from '../Fieldset/SelectFormField';
import { TextareaInputFormField } from '../Fieldset/TextareaInputFormField';
import { TextInputFormField } from '../Fieldset/TextInputFormField';
import { DropdownOption } from '../UsedCarSelectionForm/UsedCarSelectionFormModels';
import { FormField } from '..';
import { nonBreakingSpace } from '../../utils/constants';

export const createFormInputs = (formTranslationKey: string) => {
  return {
    tradingName: () => (
      <TextInputFormField
        fieldName="tradingName"
        labelTranslationKey={`${formTranslationKey}:fields:tradingName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:tradingName:placeholder`}
      />
    ),
    legalName: () => (
      <TextInputFormField
        fieldName="legalName"
        labelTranslationKey={`${formTranslationKey}:fields:legalName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:legalName:placeholder`}
      />
    ),
    vatLiable: () => (
      <SelectFormField
        fieldName="vatLiable"
        labelTranslationKey={`${formTranslationKey}:fields:vatLiable:label`}
        optionList={createOptionsList(NoYesEnum, 'companyDetailsForm', 'noYes', null)}
      />
    ),
    vatNumber: () => (
      <TextInputFormField
        fieldName="vatNumber"
        labelTranslationKey={`${formTranslationKey}:fields:vatNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:vatNumber:placeholder`}
        optional
      />
    ),
    croNumber: () => (
      <NumericInputFormField
        fieldName="croNumber"
        labelTranslationKey={`${formTranslationKey}:fields:croNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:croNumber.placeholder`}
        type="numeric"
        maximumFractionDigits={0}
        doNotFormat
      />
    ),
    croNumberButton: (isDisabled: boolean) => (
      <FormField
        name="croNumberButton"
        labelText={nonBreakingSpace} // An empty string or simple space would cause a broken layout
        isDisabled={isDisabled}
        render={() => (
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          <Button name="croNumberButton" type="button" onClick={() => {}} disabled={isDisabled} full>
            {t(`${formTranslationKey}:fields:croNumber:label`)}
          </Button>
        )}
      />
    ),
    dateOfIncorporation: () => (
      <DatePickerFormField
        fieldName="dateOfIncorporation"
        labelTranslationKey={`${formTranslationKey}:fields:dateOfIncorporation:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:dateOfIncorporation:placeholder`}
        maxDate={getMaxDateOfIncorporationDate()}
      />
    ),
    businessType: (options: Array<DropdownOption>) => (
      <SelectFormField
        fieldName="businessType"
        labelTranslationKey={`${formTranslationKey}:fields:businessType:label`}
        optionList={options}
      />
    ),
    businessSubType: (options: Array<DropdownOption>) => (
      <SelectFormField
        fieldName="businessSubType"
        labelTranslationKey={`${formTranslationKey}:fields:businessSubType:label`}
        optionList={options}
      />
    ),
    countryCode: () => (
      <SelectFormField
        fieldName="countryCode"
        labelTranslationKey={`${formTranslationKey}:fields:countryCode:label`}
        optionList={createOptionsList(CountryCodeEnum, formTranslationKey, 'countryCode')}
      />
    ),
    contactCountryCode: () => (
      <SelectFormField
        fieldName="contactCountryCode"
        labelTranslationKey={`${formTranslationKey}:fields:contactCountryCode:label`}
        optionList={createOptionsList(CountryCodeEnum, formTranslationKey, 'contactCountryCode')}
      />
    ),
    mobilePhone: () => (
      <PhoneNumberFormField
        fieldName="mobilePhone"
        labelTranslationKey={`${formTranslationKey}:fields:mobilePhone:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:mobilePhone:placeholder`}
        type="mobile"
      />
    ),
    phoneNumber: (mobilePhoneType: boolean) => {
      return mobilePhoneType ? (
        <PhoneNumberFormField
          fieldName="phoneNumber"
          labelTranslationKey="customerDetailsForm:fields:mobileNumber:label"
          placeholderTranslationKey="customerDetailsForm:fields:mobileNumber:placeholder"
          type="mobile"
        />
      ) : (
        <PhoneNumberFormField
          fieldName="phoneNumber"
          labelTranslationKey="customerDetailsForm:fields:telephoneNumber:label"
          placeholderTranslationKey="customerDetailsForm:fields:telephoneNumber:placeholder"
          type="landline"
        />
      );
    },
    contactPhoneNumber: () => (
      <PhoneNumberFormField
        fieldName="contactPhoneNumber"
        labelTranslationKey={`${formTranslationKey}:fields:contactPhoneNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:contactPhoneNumber:placeholder`}
        type="mobile"
      />
    ),
    email: () => (
      <TextInputFormField
        fieldName="email"
        labelTranslationKey={`${formTranslationKey}:fields:email:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:email:placeholder`}
      />
    ),
    fleetSize: () => (
      <NumericInputFormField
        fieldName="fleetSize"
        labelTranslationKey={`${formTranslationKey}:fields:fleetSize:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:fleetSize.placeholder`}
        type="numeric"
        maximumFractionDigits={0}
      />
    ),
    companyClassification: () => (
      <SelectFormField
        fieldName="companyClassification"
        labelTranslationKey={`${formTranslationKey}:fields:companyClassification:label`}
        optionList={createOptionsList(CompanyClassificationEnum, formTranslationKey, 'companyClassification')}
      />
    ),
    correspondenceMethod: () => (
      <SelectFormField
        fieldName="correspondenceMethod"
        labelTranslationKey={`${formTranslationKey}:fields:correspondenceMethod:label`}
        optionList={createOptionsList(CorrespondenceMethodEnum, formTranslationKey, 'correspondenceMethod')}
      />
    ),
    turnover: () => (
      <NumericInputFormField
        fieldName="turnover"
        labelTranslationKey={`${formTranslationKey}:fields:turnover:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:turnover.placeholder`}
        type="money"
      />
    ),
    identityType: () => (
      <SelectFormField
        fieldName="identityType"
        labelTranslationKey={`${formTranslationKey}:fields:identityType:label`}
        optionList={createOptionsList(IdentityTypeEnum, formTranslationKey, 'identityType')}
      />
    ),
    identityNumber: () => (
      <TextInputFormField
        fieldName="identityNumber"
        labelTranslationKey={`${formTranslationKey}:fields:identityNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:identityNumber:placeholder`}
      />
    ),
    registeredPartnershipName: () => (
      <TextInputFormField
        fieldName="registeredPartnershipName"
        labelTranslationKey={`${formTranslationKey}:fields:registeredPartnershipName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:registeredPartnershipName:placeholder`}
      />
    ),
    institutionalType: () => (
      <SelectFormField
        fieldName="institutionalType"
        labelTranslationKey={`${formTranslationKey}:fields:institutionalType:label`}
        optionList={createOptionsList(InstitutionalTypeEnum, formTranslationKey, 'institutionalType')}
      />
    ),
    entityForm: () => (
      <SelectFormField
        fieldName="entityForm"
        labelTranslationKey={`${formTranslationKey}:fields:entityForm:label`}
        optionList={createOptionsList(EntityFormEnum, formTranslationKey, 'entityForm')}
      />
    ),
    enterpriseSize: (optional: boolean) => (
      <SelectFormField
        fieldName="enterpriseSize"
        labelTranslationKey={`${formTranslationKey}:fields:enterpriseSize:label`}
        optionList={createOptionsList(EnterpriseSizeEnum, formTranslationKey, 'enterpriseSize')}
        optional={optional}
      />
    ),
    annualProfitCurrentYear: () => (
      <NumericInputFormField
        fieldName="annualProfitCurrentYear"
        labelTranslationKey={`${formTranslationKey}:fields:annualProfitCurrentYear:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:annualProfitCurrentYear.placeholder`}
        maximumFractionDigits={2}
        type="money"
      />
    ),
    annualProfitPreviousYear: () => (
      <NumericInputFormField
        fieldName="annualProfitPreviousYear"
        labelTranslationKey={`${formTranslationKey}:fields:annualProfitPreviousYear:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:annualProfitPreviousYear.placeholder`}
        maximumFractionDigits={2}
        type="money"
      />
    ),
    businessHistory: () => (
      <TextareaInputFormField
        fieldName="businessHistory"
        labelTranslationKey={`${formTranslationKey}:fields:businessHistory:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:businessHistory.placeholder`}
        maxCounter={255}
        charCounter
      />
    ),
    currentBusinessContracts: () => (
      <TextareaInputFormField
        fieldName="currentBusinessContracts"
        labelTranslationKey={`${formTranslationKey}:fields:currentBusinessContracts:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:currentBusinessContracts.placeholder`}
        maxCounter={255}
        charCounter
      />
    ),
    telephoneType: () => (
      <SelectFormField
        fieldName="telephoneType"
        labelTranslationKey={`${formTranslationKey}:fields:telephoneType:label`}
        optionList={createOptionsList(EnterpriseSizeEnum, formTranslationKey, 'telephoneType')}
      />
    ),
    firstName: () => (
      <TextInputFormField
        fieldName="firstName"
        labelTranslationKey={`${formTranslationKey}:fields:firstName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:firstName:placeholder`}
      />
    ),
    lastName: () => (
      <TextInputFormField
        fieldName="lastName"
        labelTranslationKey={`${formTranslationKey}:fields:lastName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:lastName:placeholder`}
      />
    ),
    contactFirstName: () => (
      <TextInputFormField
        fieldName="contactFirstName"
        labelTranslationKey={`${formTranslationKey}:fields:contactFirstName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:contactFirstName:placeholder`}
      />
    ),
    contactLastName: () => (
      <TextInputFormField
        fieldName="contactLastName"
        labelTranslationKey={`${formTranslationKey}:fields:contactLastName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:contactLastName:placeholder`}
      />
    ),
    contactPosition: () => (
      <TextInputFormField
        fieldName="position"
        labelTranslationKey={`${formTranslationKey}:fields:position:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:position:placeholder`}
      />
    ),
  };
};
